function normalizeNumericId(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function normalizeIdArray(values) {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .map(normalizeNumericId)
        .filter((value) => value !== null);
}

function normalizeNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
}

function normalizeBoolean(value, fallback) {
    if (typeof value === "boolean") {
        return value;
    }

    if (value === "true") {
        return true;
    }

    if (value === "false") {
        return false;
    }

    return fallback;
}

function normalizeProductCardStyle(value, fallback) {
    const allowedStyles = ["classic", "modern", "minimal", "compact", "clean", "framed"];
    return allowedStyles.includes(value) ? value : fallback;
}

function normalizeTextAlign(value, fallback) {
    const allowedValues = ["left", "center", "right"];
    return allowedValues.includes(value) ? value : fallback;
}

function normalizeHeaderColumnSpan(value, fallback) {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return fallback;
    }

    return parsed >= 2 ? 2 : 1;
}

function mergeObjectDefaults(defaults, values) {
    if (!values || typeof values !== "object" || Array.isArray(values)) {
        return defaults;
    }

    return Object.keys(defaults).reduce((accumulator, key) => {
        accumulator[key] = Object.prototype.hasOwnProperty.call(values, key)
            ? values[key]
            : defaults[key];
        return accumulator;
    }, {});
}

function looksLikeLegacyTemplate4Rows(rows) {
    return Array.isArray(rows) && rows.some((row) => {
        return row && typeof row === "object" && (
            Object.prototype.hasOwnProperty.call(row, "productGroupId") ||
            Object.prototype.hasOwnProperty.call(row, "visibleProductIds")
        );
    });
}

export function createTemplate4Row(overrides = {}) {
    return {
        productGroupId: normalizeNumericId(overrides.productGroupId),
        visibleProductIds: normalizeIdArray(overrides.visibleProductIds),
    };
}

export function createTemplate4Column(overrides = {}) {
    const rows = Array.isArray(overrides.rows)
        ? overrides.rows.map((row) => createTemplate4Row(row))
        : [];

    return {
        showHeader: normalizeBoolean(overrides.showHeader, false),
        title: typeof overrides.title === "string" ? overrides.title : "",
        subtitle: typeof overrides.subtitle === "string" ? overrides.subtitle : "",
        rows,
    };
}

export function createDefaultTemplate4Config() {
    return {
        theme: {
            lang: "fr",
            backgroundColor: "",
            textColor: "#111111",
            accentColor: "#16a34a",
            groupBackgroundColor: "#ffffff",
            titleColor: "#16a34a",
            subtitleColor: "#111111",
            columnTitleColor: "#16a34a",
            columnSubtitleColor: "#111111",
        },
        layout: {
            showPrices: true,
            showDots: true,
            showDescriptions: true,
            showProductImage: false,
            productImageSize: 64,
            columnGap: 24,
            groupSpacing: 24,
            contentPadding: 32,
            contentOffsetLeft: 0,
            contentOffsetRight: 0,
            headerBottomSpacing: 0,
            columnHeaderBottomSpacing: 8,
            headerColumnSpan: 1,
            headerAlign: "left",
            columnHeaderAlign: "left",
            showCurrencySymbol: true,
            productCardStyle: "classic",
        },
        typography: {
            titleSize: 52,
            subtitleSize: 22,
            columnTitleSize: 20,
            columnSubtitleSize: 14,
            groupTitleSize: 18,
            productNameSize: 16,
            productDescriptionSize: 13,
        },
        columns: [
            createTemplate4Column({
                rows: [createTemplate4Row()]
            })
        ]
    };
}

export function normalizeTemplate4Config(rawConfig) {
    const defaults = createDefaultTemplate4Config();
    const source = rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig)
        ? rawConfig
        : {};

    let columns = [];

    if (Array.isArray(source.columns) && source.columns.length) {
        columns = source.columns.map((column) => createTemplate4Column(column));
    } else if (looksLikeLegacyTemplate4Rows(source.rows)) {
        columns = [
            createTemplate4Column({
                rows: source.rows.map((row) => createTemplate4Row(row))
            })
        ];
    }

    return {
        ...defaults,
        title: typeof source.title === "string" ? source.title : defaults.title,
        subtitle: typeof source.subtitle === "string" ? source.subtitle : defaults.subtitle,
        theme: mergeObjectDefaults(defaults.theme, source.theme),
        layout: {
            ...mergeObjectDefaults(defaults.layout, source.layout),
            showPrices: normalizeBoolean(source?.layout?.showPrices, defaults.layout.showPrices),
            showDots: normalizeBoolean(source?.layout?.showDots, defaults.layout.showDots),
            showDescriptions: normalizeBoolean(source?.layout?.showDescriptions, defaults.layout.showDescriptions),
            showProductImage: normalizeBoolean(source?.layout?.showProductImage, defaults.layout.showProductImage),
            productImageSize: normalizeNumber(source?.layout?.productImageSize, defaults.layout.productImageSize),
            columnGap: normalizeNumber(source?.layout?.columnGap, defaults.layout.columnGap),
            groupSpacing: normalizeNumber(source?.layout?.groupSpacing, defaults.layout.groupSpacing),
            contentOffsetLeft: normalizeNumber(source?.layout?.contentOffsetLeft, defaults.layout.contentOffsetLeft),
            contentOffsetRight: normalizeNumber(source?.layout?.contentOffsetRight, defaults.layout.contentOffsetRight),
            headerBottomSpacing: normalizeNumber(
                source?.layout?.headerBottomSpacing,
                defaults.layout.headerBottomSpacing
            ),
            columnHeaderBottomSpacing: normalizeNumber(
                source?.layout?.columnHeaderBottomSpacing,
                defaults.layout.columnHeaderBottomSpacing
            ),
            headerColumnSpan: normalizeHeaderColumnSpan(
                source?.layout?.headerColumnSpan,
                defaults.layout.headerColumnSpan
            ),
            headerAlign: normalizeTextAlign(source?.layout?.headerAlign, defaults.layout.headerAlign),
            columnHeaderAlign: normalizeTextAlign(
                source?.layout?.columnHeaderAlign,
                defaults.layout.columnHeaderAlign
            ),
            showCurrencySymbol: normalizeBoolean(
                source?.layout?.showCurrencySymbol,
                defaults.layout.showCurrencySymbol
            ),
            productCardStyle: normalizeProductCardStyle(
                source?.layout?.productCardStyle,
                defaults.layout.productCardStyle
            ),
        },
        typography: {
            ...mergeObjectDefaults(defaults.typography, source.typography),
            titleSize: normalizeNumber(source?.typography?.titleSize, defaults.typography.titleSize),
            subtitleSize: normalizeNumber(source?.typography?.subtitleSize, defaults.typography.subtitleSize),
            columnTitleSize: normalizeNumber(source?.typography?.columnTitleSize, defaults.typography.columnTitleSize),
            columnSubtitleSize: normalizeNumber(
                source?.typography?.columnSubtitleSize,
                defaults.typography.columnSubtitleSize
            ),
            groupTitleSize: normalizeNumber(source?.typography?.groupTitleSize, defaults.typography.groupTitleSize),
            productNameSize: normalizeNumber(source?.typography?.productNameSize, defaults.typography.productNameSize),
            productDescriptionSize: normalizeNumber(
                source?.typography?.productDescriptionSize,
                defaults.typography.productDescriptionSize
            ),
        },
        columns: columns.length ? columns : defaults.columns,
    };
}
